import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getTourStep, saveTourStep, clearTourStep } from "./onboardingStorage";

// ─── SPOTLIGHT COACH MARK TOUR ─────────────────────────────────────────────
// Dims the whole screen except one real element at a time (found via the
// data-tour attributes sprinkled through App.jsx / HabitsTab.jsx / CalendarTab.jsx
// / FocusTab.jsx), with a tooltip explaining it. This one component runs three
// separate tours — the main post-onboarding tour (built by buildOnboardingTourSteps,
// the default when no `steps` prop is given) plus the Planner and Clock-In
// tours below (buildPlannerTourSteps / CLOCKIN_TOUR_STEPS, passed in explicitly) —
// each triggered independently from App.jsx.
//
// Behavior:
//  - No Next/Skip buttons. Tapping ANYWHERE always advances to the next step
//    (or finishes the tour on the last one) — no exceptions, so the tour can
//    never leave the user stuck on a panel with no way forward. What varies
//    by step is only how much of the screen stays interactive underneath:
//     • `interactive: true` — the copy asks the user to tap one specific
//       control. A hole is left over the spotlight so that control keeps
//       working normally (tapping it both performs its action AND advances);
//       everywhere else is blocked, so a tap elsewhere can't also poke some
//       unrelated part of the app.
//     • `passive: true` — the step is explaining something that lives inside
//       a form or panel the user might still want to use (e.g. the New habit
//       form's To Do/Restricted toggle). No blocker at all, so every field in
//       that form keeps working — a tap there both interacts with the form
//       AND advances the tour, same as anywhere else.
//     • plain (neither flag) — a simple explanation with nothing else to
//       interact with (the nav bar, an already-open list). Blocks the whole
//       screen while showing, so the advancing tap can't double as a tap on
//       the app underneath (e.g. dismissing a modal's backdrop instead of
//       just moving the tour along).
//  - If a step's target isn't in the DOM yet, or is covered by something else
//    (a modal on top of it), the tour renders NOTHING at all — no spotlight,
//    no dim, no floating panel — until it's genuinely on screen. Every panel
//    that DOES show is the real spotlight-and-tooltip pair; there's no other
//    kind of panel the tour ever displays.
//  - Advances are debounced by elapsed time (MIN_ADVANCE_GAP_MS), not by a
//    one-shot "armed" flag — see the comment on `advance` for why.
//  - A tap only ever advances the step that's actually on screen: if the
//    current step's spotlight hasn't appeared yet (target not found, still
//    obstructed, or simply hasn't rendered its first frame), an unrelated
//    tap on the real app underneath does nothing — it does NOT eat that
//    step's turn. Otherwise an ordinary tap made while navigating toward a
//    step's target could silently skip that step before its spotlight had
//    ever appeared. See the comment on `advance`.
//  - Every step is given STEP_TIMEOUT_MS to show its target before the tour
//    gives up on it and moves on — this is what keeps the tour from getting
//    permanently stuck. It matters even for non-`optional` steps: tap-
//    anywhere-advances means a step immediately after an `interactive` one
//    (e.g. "habit-type-toggle", which only exists once the add-habit form
//    fab-add opens is actually open) can be advanced INTO without the user
//    ever having tapped the control that would have made its target exist —
//    at that point the tour has already moved past the one step that could
//    have produced it, so waiting forever is not "the user hasn't acted
//    yet", it's stuck for good. `optional: true` marks a step whose target
//    may genuinely never exist this session at all (not just skipped past)
//    — e.g. the Journal tour's "From your spaces" suggestions, which
//    JournalTab only renders when there's an actual Space to suggest — but
//    the timeout itself now applies to every step, optional or not.
//  - In the main tour, steps 4 & 5 (the "customize a habit" pair) only run
//    for users who already have at least one habit card to point at — i.e.
//    they picked starter habits during onboarding. That's decided once, when
//    the tour mounts, from the `habits` list handed down from App.jsx.
export function buildOnboardingTourSteps(hasHabits) {
  const steps = [
    { selector: '[data-tour="nav-bar"]', title: "Get around Clarity", body: "Jump between Habits, Planner, Clock-In, and Journal here." },
    { selector: '[data-tour="fab-add"]', interactive: true, title: "Add habits", body: "Tap + to add a new habit." },
    { selector: '[data-tour="habit-type-toggle"]', passive: true, title: "To Do vs. Restricted", body: "To Do = habits to build. Restricted = habits to cut back or quit." },
  ];
  if (hasHabits) {
    steps.push(
      { selector: '[data-tour="first-habit-card"]', interactive: true, title: "See habit details", body: "Tap the card to see and customize this habit." },
      { selector: '[data-tour="habit-edit-btn"]', interactive: true, title: "Make it yours", body: "Tap edit to customize it." },
    );
  }
  return steps;
}

// Triggered the first time the user opens the Planner tab. The "+" resolves to
// whichever add button this view actually shows — the small one in the topbar
// in calendar view, or the big FAB in list view; both carry the same anchor.
//
// The swipe-navigation step only applies on mobile — desktop's calendar grid
// has no swipe gesture at all (CalendarTab.jsx renders a plain, non-swipeable
// pane there; see `renderCalCarousel`'s `isDesktop` early return) — and its
// target ([data-tour="planner-calendar-grid"]) only exists while the user is
// actually on the calendar sub-view rather than the list sub-view. It's
// `optional` for that second reason: a user who opens Planner straight into
// list view (their last-used sub-view, persisted from before) may simply not
// have that target on screen this session, same as Journal's Spaces step.
//
// On a phone the calendar views have NO "+" (Week adds by tapping a time slot,
// Month through a day), so starting there the "+" step is left out instead of
// being waited on: it sat invisible for its whole 6s timeout before the tour
// showed anything (Jade, 2026-10-11). The grid step says how to add instead.
export function buildPlannerTourSteps(isMobile, plannerView = "calendar") {
  const mobileCalendar = isMobile && plannerView !== "list";
  const steps = [];
  if (!mobileCalendar) {
    steps.push({ selector: '[data-tour="planner-add-btn"]', interactive: true, title: "Add tasks & events", body: "Tap + to add a task or event." });
  }
  steps.push({ selector: '[data-tour="planner-view-toggle"]', passive: true, title: "Choose your view", body: "Switch between Week, Month, and Year here." });
  if (isMobile) {
    steps.push({ selector: '[data-tour="planner-calendar-grid"]', optional: true, dockBottomMobile: true, title: "Swipe to move around",
      body: mobileCalendar
        ? "Swipe left or right to move between weeks, months, or years. To add a task or event, tap a time slot in Week, or a day in Month."
        : "Swipe left or right on the calendar to move between weeks, months, or years." });
  }
  return steps;
}

// A second, later Planner tour (Jade, 2026-10-11), run once the first one is
// done — so people who already finished that one still get these. Its own
// done flag, so adding steps here never replays the first tour.
//  - The List button (mobile only: the quick-add bar is a phone feature).
//  - The Week view's lock button. That button only exists in Week view, so the
//    step `waitForTarget`s: no timeout, it simply shows the first time the user
//    is on Week view (the tour resumes there via its persistKey).
export function buildPlannerExtrasTourSteps(isMobile) {
  const steps = [];
  if (isMobile) {
    steps.push({ selector: '[data-tour="planner-list-btn"]', title: "Your list view", body: "See your tasks & events in one scrollable list. Tap + there to quick-add from a bar right above the keyboard, if you don't need the full form." });
  }
  steps.push({ selector: '[data-tour="planner-week-lock"]', waitForTarget: true, title: "Lock your blocks", body: "Lock this to scroll freely without accidentally moving or resizing a task/event block. Unlock it when you want to resize one." });
  return steps;
}

// Triggered the first time the user opens the Clock-In tab. The mode button
// comes first (Jade, 2026-10-24), then Select, which opens the assign panel; the
// two after it explain that panel while it's open, so they block taps to keep the
// panel from being dismissed underneath them (its backdrop closes it on any tap).
export const CLOCKIN_TOUR_STEPS = [
  { selector: '[data-tour="clockin-mode-btn"]', title: "Timer or stopwatch", body: "Tap to switch between Custom (a countdown) and Elapsed (counts up)." },
  { selector: '[data-tour="clockin-todo-btn"]', interactive: true, title: "Pick what to work on", body: "Tap to choose a habit, task or category." },
  // On mobile the panel is a bottom sheet tall enough to leave no good room
  // above, below, or beside it for a floating tooltip — dockBottomMobile
  // pins this one to the bottom of the screen instead of trying to track
  // the panel itself. Desktop's panel is a smaller centered modal with
  // room around it, so it keeps the normal dynamic placement there.
  { selector: '[data-tour="assign-todo-panel"]', title: "Everything in one place", body: "Your habits and tasks show up here. Tap one, then Continue.", dockBottomMobile: true },
  // Points at the panel's Tasks tab, which is always on screen: the "Add a custom
  // to-do" button itself only exists on that tab, and the panel opens on Habits,
  // so the step used to wait out its whole timeout (Jade, 2026-10-11).
  { selector: '[data-tour="clockin-tasks-tab"]', title: "Something else in mind?", body: "Under Tasks you can add a quick custom to-do." },
];

// A second Clock-In tour for the Chart page (Jade, 2026-10-24), run once the first
// one is done, so people who already finished that one get it too. Its first step
// waits for the Chart button to be on screen and uncovered, i.e. after the assign
// panel the first tour leaves open has been closed. The logged sessions list only
// exists once something is logged, so that step is optional.
export const CLOCKIN_CHART_TOUR_STEPS = [
  { selector: '[data-tour="clockin-chart-btn"]', interactive: true, waitForTarget: true, title: "Your logged time", body: "Tap to see your logged sessions." },
  { selector: '[data-tour="clockin-donut"]', waitForTarget: true, title: "Your time, all in one place", body: "Every session you finish will be added here, grouped by category. You'll see at a glance what you spend the most time on." },
  { selector: '[data-tour="clockin-logged-sessions"]', optional: true, title: "Every session", body: "Every session you log is listed here. Tap a category to see each one's duration, time and details." },
];

// Triggered the first time the user opens the Journal tab. Mobile and desktop
// lay the folder flow out completely differently — a bottom drawer vs. a
// dropdown beside the toolbar — but both carry the same anchors, so one step
// list drives both and each step resolves to whichever variant is rendered.
// Only the toolbar copy forks, since horizontal scrolling is a mobile problem.
export function buildJournalTourSteps(isMobile) {
  return [
    { selector: '[data-tour="journal-add-btn"]', interactive: true, title: "Start writing", body: "Tap + to start a new entry." },
    { selector: '[data-tour="journal-folder-btn"]', interactive: true, title: "Keep notes sorted", body: "Tap to file this note into a folder." },
    { selector: '[data-tour="journal-new-folder-btn"]', interactive: true, title: "Make a folder", body: "Create a new folder for this note." },
    // Only rendered by JournalTab when the user actually has an unused Space
    // (or "Uncategorized" isn't already a folder) to suggest — genuinely
    // absent for some users, not just "not there yet". Flagged optional so
    // the tour times out past it instead of waiting forever for a target
    // that will never exist for this session.
    { selector: '[data-tour="journal-spaces-section"]', optional: true, title: "Your Categories, ready to use", body: "Tap a Category to file this note into it." },
    {
      selector: '[data-tour="journal-toolbar"]',
      title: "Your writing toolbar",
      body: isMobile
        ? "Format your text: bold, headings, lists, images, and more. Scroll sideways for more tools."
        : "Format your text: bold, headings, lists, images, and more.",
    },
  ];
}

// Triggered the first time the user opens the Spaces tab (mobile only — the
// desktop dashboard folds categories into the Habits tab's add menu).
export const SPACES_TOUR_STEPS = [
  { selector: '[data-tour="spaces-add-btn"]', interactive: true, title: "Add a Category", body: "Tap + to create a new Category." },
];

// Shown once each, the first time a Full Stop / Limited habit's shield chip is on
// the Habits tab (Jade, 2026-10-26): the chip has no words, so these say what it
// is. Two tours because the number means something slightly different for each.
export const FULLSTOP_TOUR_STEPS = [
  { selector: '[data-tour="fullstop-chip"]', waitForTarget: true, title: "Your clean streak", body: "This shows how many days in a row you've stayed away from it. Slipped? Tap it to log it." },
];
export const LIMITED_TOUR_STEPS = [
  { selector: '[data-tour="limited-chip"]', waitForTarget: true, title: "Your clean streak", body: "This shows how many days in a row you've stayed within your limit. Slipped? Tap it to log it." },
];

export const PAD = 8;
const POLL_MS = 100;
// Minimum time between two advances — long enough to outlast a form's own
// opening animation plus the tour's ~0.3s fade-in, so an impatient second tap
// (made because the first didn't *look* like it did anything yet) can't skip
// the step it just revealed before it's had a chance to actually appear.
const MIN_ADVANCE_GAP_MS = 700;
// How long a step is allowed to sit with its target genuinely absent before
// the tour gives up on it and moves on.
//
// An `optional` step's target may simply not exist for this user, so there's
// nothing to wait for and a short window keeps the tour moving. Every other
// step gets a much longer one, because giving up early there is far more
// costly than a slow recovery: skipping through the remaining steps ends the
// tour and marks it done permanently, so a tab that merely took a while to
// paint (a heavy tab mounting for the first time on a slow phone, a panel
// still animating in) would burn its guide for good and never show it again.
// Long enough to clear any realistic mount, still short enough that a
// genuinely unreachable target doesn't look like a hang.
const OPTIONAL_STEP_TIMEOUT_MS = 2500;
const STEP_TIMEOUT_MS = 6000;
const NAV_SAFE_PX = 118;  // bottom nav height + breathing room, so panels never sit over the tabs
const TIP_MARGIN = 14;
// Above everything the app itself paints — the habits/planner FABs sit at
// z-index 99999, so anything lower would leave them floating over the dim and,
// worse, outside the blocker.
export const Z_BLOCK = 100000;
export const Z_SPOT = 100001;
export const Z_TIP = 100002;

// Rects come back as fresh objects every poll tick; compare by value so an
// unmoved target doesn't churn React state (and restart the fade) 5x a second.
const sameRect = (a, b) =>
  Math.abs(a.top - b.top) < 0.5 && Math.abs(a.left - b.left) < 0.5 &&
  Math.abs(a.width - b.width) < 0.5 && Math.abs(a.height - b.height) < 0.5;

// One shared stylesheet for the tour's entrance animations, injected once.
export const ANIM_CSS = `
@keyframes clarity-coach-tip-in {
  from { opacity: 0; transform: translateY(6px) scale(0.985); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes clarity-coach-spot-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}
.clarity-coach-tip  { animation: clarity-coach-tip-in 0.28s cubic-bezier(0.22, 1, 0.36, 1) both; }
.clarity-coach-spot { animation: clarity-coach-spot-in 0.3s ease both; }
@media (prefers-reduced-motion: reduce) {
  .clarity-coach-tip, .clarity-coach-spot { animation-duration: 0.01s; }
}
`;

// Resolves a step's target element, or null if it's not in the DOM, has no
// size, or is currently covered by something else on screen (e.g. a modal
// sitting on top of it) — all treated the same as "not there yet".
//
// Uses querySelectorAll, not querySelector: some anchors are duplicated across
// mobile/desktop variants (there are two [data-tour="fab-add"] buttons in
// HabitsTab), and only one of them is ever the real, rendered one. Taking the
// first *visible* match rather than the first match in the DOM is what keeps
// the tour pointed at the button the user can actually see.
function findTarget(step) {
  if (!step) return null;
  for (const el of document.querySelectorAll(step.selector)) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return el;
  }
  return null;
}

// Whether a found target is actually the thing on screen at its own position.
// False when a modal covers it, and also when it's scrolled out of (or clipped
// by) a scroll container — in that second case the caller scrolls it into view
// and this flips true on a later tick.
//
// Has to look past the tour's OWN chrome first: a non-interactive step's
// full-screen tap blocker sits directly on top of the target (that's the
// point of it), so a naive elementFromPoint check would see the blocker
// itself and conclude the target is obstructed — which hides the tour,
// which removes the blocker, which un-obstructs the target again, which
// shows the tour and the blocker again, forever. Every element the tour
// renders carries data-clarity-tour-chrome, so it's skipped here in favour
// of whatever real app content is underneath it.
function isUnobstructed(el) {
  const r = el.getBoundingClientRect();
  const vh = window.innerHeight, vw = window.innerWidth;
  if (r.bottom <= 0 || r.top >= vh || r.right <= 0 || r.left >= vw) return false;
  const stack = document.elementsFromPoint(r.left + r.width / 2, r.top + r.height / 2);
  for (const node of stack) {
    if (node.closest("[data-clarity-tour-chrome]")) continue;
    return node === el || el.contains(node);
  }
  return true;
}

function CoachMarkTour({ steps: stepsProp, habits = [], onStepChange, onFinish, persistKey }) {
  const [steps] = useState(() => stepsProp || buildOnboardingTourSteps((habits?.length || 0) > 0));
  // Per-tab tours are torn down (unmounted) the moment the user leaves their
  // tab — a step's target only exists on that one tab, so there's nothing
  // useful for it to keep polling for elsewhere — and rebuilt from scratch
  // the next time that tab opens. A tour that takes more than one visit to
  // finish (Journal's 5 steps, several needing a real action first) would
  // otherwise restart at step 1 forever, since it never lives long enough in
  // one sitting to reach the last step that marks it done. `persistKey` (the
  // main tour omits it — it can't be interrupted this way, since leaving its
  // one tab ends it outright) resumes from wherever the last mount left off.
  const [stepIndex, setStepIndex] = useState(() => {
    if (!persistKey) return 0;
    const saved = getTourStep(persistKey);
    return saved > 0 && saved < steps.length ? saved : 0;
  });
  const [rect, setRect] = useState(null);
  // Seeded at mount (not 0) so the very tap that triggered the tour itself
  // can't also be counted as the first advance.
  const lastAdvanceAtRef = useRef(Date.now());
  const lastElRef = useRef(null);     // the element we're currently latched onto, so we only scroll to it once
  const rectRef = useRef(null);       // mirrors `rect` for the click handler below, which can't see React state
  const stepStartAtRef = useRef(Date.now()); // when the current step became current, for the step timeout

  useEffect(() => { rectRef.current = rect; }, [rect]);
  useEffect(() => { stepStartAtRef.current = Date.now(); }, [stepIndex]);
  useEffect(() => {
    if (persistKey) saveTourStep(persistKey, stepIndex);
  }, [persistKey, stepIndex]);

  const goToNextStep = useCallback(() => {
    lastAdvanceAtRef.current = Date.now();
    setStepIndex(i => {
      if (i >= steps.length - 1) {
        if (persistKey) clearTourStep(persistKey);
        onFinish?.();
        return i;
      }
      return i + 1;
    });
  }, [steps.length, onFinish, persistKey]);

  // Every step's target gets a timeout to show up genuinely on screen —
  // found AND unobstructed — before the tour gives up on it and moves on.
  // Covers two different ways of never getting there, not just one:
  //  - Not found at all: a step right after an `interactive` one can be
  //    advanced into without the user ever having tapped the control that
  //    would have made its target exist (tap-anywhere-advances doesn't
  //    require the tap to land on the hole), and at that point there's no
  //    going back to trigger it — so waiting indefinitely means forever.
  //  - Found but permanently obstructed: a target sitting inside a scroll
  //    container that scrollIntoView can't actually resolve (fighting the
  //    container's own scroll snapping, or a target that's occluded by
  //    something the tour doesn't know how to see past) polls in this same
  //    "not on screen yet" state and never recovers on its own either.
  // Without a timeout on both, the tour is left running — not finished, not
  // visibly showing anything — so it never calls onFinish, its "done" flag
  // never gets set, and every later visit to that tab restarts it from step
  // one: from the user's side, a guide that supposedly "already showed"
  // keeps coming back.
  const bailOnStep = useCallback(() => {
    // A step that can only appear in one view (e.g. Week view's lock) waits
    // for the user to get there instead of being skipped.
    if (steps[stepIndex]?.waitForTarget) return;
    const timeout = steps[stepIndex]?.optional ? OPTIONAL_STEP_TIMEOUT_MS : STEP_TIMEOUT_MS;
    if (Date.now() - stepStartAtRef.current > timeout) {
      goToNextStep();
    }
  }, [stepIndex, steps, goToNextStep]);

  const measure = useCallback(() => {
    const el = findTarget(steps[stepIndex]);
    if (!el) {
      lastElRef.current = null;
      setRect(prev => (prev === null ? prev : null));
      bailOnStep();
      return;
    }
    // Only scroll when we first latch onto a given element — re-running this
    // every poll tick fights the user's own scrolling and makes the spotlight
    // jitter.
    if (lastElRef.current !== el) {
      lastElRef.current = el;
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
    // The target exists but something's in front of it, or it's clipped out of
    // a scroll container (e.g. "Add a custom to-do" sitting below the fold of
    // the assign panel's list). Nudge it into view and stay in the waiting
    // state; a later tick picks it up once it's genuinely on screen — but if
    // it never does, bailOnStep still moves the tour on instead of leaving it
    // stuck here for good.
    if (!isUnobstructed(el)) {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
      setRect(prev => (prev === null ? prev : null));
      bailOnStep();
      return;
    }
    const r = el.getBoundingClientRect();
    // Bail out of the state update unless the box actually moved. Polling at
    // 5fps with a fresh object every tick would otherwise re-render (and
    // restart the tooltip's fade) constantly.
    setRect(prev => (prev && sameRect(prev, r) ? prev : r));
  }, [stepIndex, steps, bailOnStep]);

  useEffect(() => {
    onStepChange?.(stepIndex);
  }, [stepIndex, onStepChange]);

  // Debounced by elapsed time, not by an "armed" flag tied to effect re-runs —
  // a flag that gets reset by a step-change effect is only as reliable as
  // that effect's own timing, and re-arming after a fixed 300ms turned out to
  // be shorter than a person's own "did that tap register?" reflex: the form
  // opening has its own animation, the tour's spotlight fades in over ~0.3s,
  // and a slightly impatient second tap landing anywhere after that window
  // would sail straight past the very step it just revealed, before anyone
  // had a chance to see it. Comparing directly against a timestamp doesn't
  // depend on any effect having already re-run, so it can't race with one.
  const advance = useCallback(() => {
    // Nothing is actually being shown yet (target not found/obstructed, or
    // the current step just hasn't rendered its first frame) — an unrelated
    // tap on the real app underneath must NOT eat this step's turn. Without
    // this, ordinary taps made while navigating toward a step's target (e.g.
    // filling out a form) could advance the tour's internal index past a
    // step before its spotlight ever had a chance to appear, so it would
    // silently never show at all. Tap-anywhere-advances still applies in
    // full once a step's spotlight is genuinely on screen.
    if (!rectRef.current) return;
    const now = Date.now();
    if (now - lastAdvanceAtRef.current < MIN_ADVANCE_GAP_MS) return;
    goToNextStep();
  }, [goToNextStep]);

  // Poll continuously (not just once) — a step's target may not exist yet, and
  // we want to pick it up the moment the user's own action (opening a form,
  // opening the assign panel) makes it appear.
  useEffect(() => {
    measure();
    const poll = setInterval(measure, POLL_MS);
    window.addEventListener("resize", measure);
    return () => {
      clearInterval(poll);
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  // Advance on ANY tap, unconditionally — captured before it reaches its
  // target so it still registers even when the target (or a modal it's
  // inside) calls stopPropagation() in the bubble phase. No gating on the
  // current step's kind or on whether its target is visible beyond the
  // debounce in `advance` itself: this is the one guarantee that keeps the
  // tour from ever stranding someone on a panel with no way forward.
  useEffect(() => {
    document.addEventListener("click", advance, true);
    return () => document.removeEventListener("click", advance, true);
  }, [advance]);

  const step = steps[stepIndex];
  if (!step) return null;

  const tipBase = {
    position: "fixed",
    zIndex: Z_TIP,
    pointerEvents: "none", // purely informational — taps pass through to whatever's beneath
    background: "var(--c-bg-card, #fff)",
    borderRadius: 18,
    boxShadow: "0 10px 32px rgba(0,0,0,0.28)",
    fontFamily: "'Quicksand', sans-serif",
  };
  const stepLabel = (
    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: "var(--c-text-soft, #999)", marginBottom: 6 }}>
      STEP {stepIndex + 1} OF {steps.length}
    </div>
  );

  // This step's target isn't on screen yet (or is covered by something) —
  // render nothing at all rather than any kind of standalone panel. Every
  // panel the tour ever shows is the real spotlight-and-tooltip pair below;
  // there's no other kind.
  if (!rect) return null;

  const viewportW = typeof window !== "undefined" ? window.innerWidth : 400;
  const viewportH = typeof window !== "undefined" ? window.innerHeight : 800;
  const spotTop = rect.top - PAD;
  const spotBottom = spotTop + rect.height + PAD * 2;
  const spotLeft = rect.left - PAD;
  const spotW = rect.width + PAD * 2;
  const spotH = rect.height + PAD * 2;
  // A step can opt out of the dynamic placement below entirely and just
  // dock to the bottom of the screen, on mobile only — the Clock-In assign
  // panel is a bottom sheet tall enough to leave no good room above,
  // below, or beside it on a phone, so tracking it dynamically kept
  // landing on a cramped/clipped placement. Desktop's version of the same
  // panel is a smaller centered modal with room around it, so it's left on
  // the normal dynamic placement there (DESKTOP_BREAKPOINT matches the
  // app's own isDesktop threshold in App.jsx).
  const DESKTOP_BREAKPOINT = 1024;
  const dockBottom = step.dockBottomMobile && viewportW < DESKTOP_BREAKPOINT;

  // Four candidate placements, tried in this order: below, above, right,
  // left. A short target (a button) almost always fits below or above. A
  // tall one (the Clock-In assign panel, which can span nearly the full
  // viewport height) has no usable room in either direction — for those,
  // fall back to whichever side of it has more horizontal room instead of
  // squeezing the tooltip into a sliver of space above/below and clipping
  // it off-screen. TIP_H_ESTIMATE/TIP_W_MIN are only used to judge whether
  // a direction has *enough* room — the actual placement never anchors off
  // a guessed size: below/above anchor by `top`/`bottom` from the
  // spotlight's own edge (flush against it regardless of the tooltip's
  // real rendered height), and left/right anchor by `left`/`right` from
  // the spotlight's own edge the same way.
  let tipPos;
  if (dockBottom) {
    tipPos = { bottom: 20, left: TIP_MARGIN, width: viewportW - TIP_MARGIN * 2 };
  } else {
    const TIP_H_ESTIMATE = 140;
    const TIP_W_MIN = 220;
    const roomBelow = viewportH - NAV_SAFE_PX - spotBottom - TIP_MARGIN;
    const roomAbove = spotTop - TIP_MARGIN - 16;
    const roomRight = viewportW - (spotLeft + spotW) - TIP_MARGIN - 16;
    const roomLeft = spotLeft - TIP_MARGIN - 16;

    let placement;
    if (roomBelow >= TIP_H_ESTIMATE) placement = "below";
    else if (roomAbove >= TIP_H_ESTIMATE) placement = "above";
    else if (roomRight >= TIP_W_MIN && roomRight >= roomLeft) placement = "right";
    else if (roomLeft >= TIP_W_MIN) placement = "left";
    // Nothing has enough room (a target filling nearly the whole screen) —
    // fall back to whichever direction has the most, rather than one that's
    // arbitrarily first in the list.
    else placement = [["below", roomBelow], ["above", roomAbove], ["right", roomRight], ["left", roomLeft]]
      .sort((a, b) => b[1] - a[1])[0][0];

    const TIP_W_VERTICAL = Math.min(360, viewportW - TIP_MARGIN * 2); // used by below/above
    if (placement === "below" || placement === "above") {
      // Horizontally centered on the spotlight (not the viewport) — a fixed
      // "left:16, right:16, margin:0 auto" centers across the FULL screen
      // width, which is fine on a narrow phone but leaves the panel stranded
      // nowhere near a target that's off to one side on a wide desktop window.
      const spotCenterX = spotLeft + spotW / 2;
      const rawLeft = spotCenterX - TIP_W_VERTICAL / 2;
      const tipLeft = Math.max(TIP_MARGIN, Math.min(rawLeft, viewportW - TIP_MARGIN - TIP_W_VERTICAL));
      tipPos = {
        // NAV_SAFE_PX only matters for a `top`-anchored panel that might land
        // near the bottom nav — it doesn't apply to `bottom`-anchoring here,
        // and flooring the bottom value at NAV_SAFE_PX regardless forced a
        // phantom gap above the target even when the natural flush position
        // was already smaller (i.e. whenever the target itself sat well above
        // the bottom nav, which is the common case for "above" placement).
        ...(placement === "below" ? { top: Math.max(16, spotBottom + TIP_MARGIN) } : { bottom: Math.max(16, viewportH - spotTop + TIP_MARGIN) }),
        left: tipLeft,
        width: TIP_W_VERTICAL,
      };
    } else {
      // Vertically centered on the spotlight, clamped to stay on screen.
      const TIP_W_SIDE = Math.min(300, (placement === "right" ? roomRight : roomLeft) - 8);
      const TIP_H_SIDE_ESTIMATE = 140; // only for clamping the centered position, not for a flush anchor
      const spotCenterY = spotTop + spotH / 2;
      const rawTop = spotCenterY - TIP_H_SIDE_ESTIMATE / 2;
      const tipTop = Math.max(16, Math.min(rawTop, viewportH - NAV_SAFE_PX - TIP_H_SIDE_ESTIMATE));
      tipPos = {
        top: tipTop,
        width: TIP_W_SIDE,
        ...(placement === "right" ? { left: spotLeft + spotW + TIP_MARGIN } : { right: viewportW - spotLeft + TIP_MARGIN }),
      };
    }
  }

  // Transparent tap blocker. Plain informational steps cover the whole screen
  // so "tap anywhere to continue" can't double as a tap on the app underneath
  // (e.g. dismissing a modal's backdrop instead of just moving the tour
  // along). Interactive steps leave a hole over the spotlight (four bands
  // around it) so that one control still receives taps. Passive steps get NO
  // blocker at all — the whole point is that the form they're annotating
  // stays completely usable while they're shown.
  const block = (extra) => ({ position: "fixed", zIndex: Z_BLOCK, background: "transparent", ...extra });
  const blockers = step.passive
    ? []
    : step.interactive
    ? [
        block({ top: 0, left: 0, right: 0, height: Math.max(0, spotTop) }),
        block({ top: spotBottom, left: 0, right: 0, bottom: 0 }),
        block({ top: spotTop, left: 0, width: Math.max(0, spotLeft), height: spotH }),
        block({ top: spotTop, left: spotLeft + spotW, right: 0, height: spotH }),
      ]
    : [block({ inset: 0 })];

  return createPortal(
    <>
      <style>{ANIM_CSS}</style>
      {blockers.map((s, i) => <div key={`block-${i}`} data-clarity-tour-chrome style={s} />)}
      <div
        data-clarity-tour-chrome
        className="clarity-coach-spot"
        style={{
          position: "fixed",
          top: spotTop,
          left: spotLeft,
          width: spotW,
          height: spotH,
          borderRadius: 18,
          boxShadow: "0 0 0 9999px rgba(35,20,35,0.68)",
          pointerEvents: "none",
          zIndex: Z_SPOT,
          // Eased so the spotlight glides between targets instead of snapping.
          transition: "top 0.34s cubic-bezier(0.22, 1, 0.36, 1), left 0.34s cubic-bezier(0.22, 1, 0.36, 1), width 0.34s cubic-bezier(0.22, 1, 0.36, 1), height 0.34s cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      />
      <div
        key={`tip-${stepIndex}`}
        data-clarity-tour-chrome
        className="clarity-coach-tip"
        style={{
          ...tipBase,
          ...tipPos,
          padding: "18px 20px",
          transition: "top 0.34s cubic-bezier(0.22, 1, 0.36, 1), bottom 0.34s cubic-bezier(0.22, 1, 0.36, 1), left 0.34s cubic-bezier(0.22, 1, 0.36, 1), right 0.34s cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      >
        {stepLabel}
        <div style={{ fontSize: 16, fontWeight: 700, color: "var(--c-text-accent, #333)", marginBottom: 4 }}>{step.title}</div>
        <div style={{ fontSize: 13.5, color: "var(--c-text-mid, #555)", lineHeight: 1.45 }}>{step.body}</div>
      </div>
    </>,
    document.body
  );
}

export default CoachMarkTour;
